import { apiService } from './api';
import { ApiResponse } from '../types';

export interface ItemBreakdownItem {
  item_id: number;
  item_name: string;
  item_brand: string;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
  total_loss: number;
  total_quantity: number;
  total_sales: number;
}

export interface ItemBreakdown {
  category: string;
  total_revenue: number;
  total_cost: number;
  total_profit: number;
  total_loss: number;
  total_quantity: number;
  total_sales: number;
  profit_loss: number;
  is_profit: boolean;
  items: ItemBreakdownItem[];
}

export interface FinanceData {
  total_revenue: number;
  total_cost: number;
  profit_loss: number;
  total_profit?: number;
  total_loss?: number;
  is_profit: boolean;
  total_sales?: number;
  total_quantity?: number;
  date?: string;
  period?: string;
  year?: number;
  start_date?: string;
  end_date?: string;
  daily_breakdown?: FinanceData[];
  item_breakdown?: ItemBreakdown[];
}

export interface FinanceSummary {
  overall: FinanceData;
  today: FinanceData & { date: string; total_sales: number };
  this_month: FinanceData & { period: string; total_sales: number };
  this_year: FinanceData & { year: number; total_sales: number };
}

export const financeService = {
  async getDailyFinance(date?: string, category?: string, itemId?: number): Promise<ApiResponse<FinanceData>> {
    const params = new URLSearchParams();
    if (date) params.append('date', date);
    if (category) params.append('category', category);
    if (itemId) params.append('item_id', itemId.toString());
    const queryString = params.toString();
    return apiService.get(`/api/finance/daily${queryString ? '?' + queryString : ''}`);
  },

  async getMonthlyFinance(month?: string, category?: string, itemId?: number): Promise<ApiResponse<FinanceData>> {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (category) params.append('category', category);
    if (itemId) params.append('item_id', itemId.toString());
    const queryString = params.toString();
    return apiService.get(`/api/finance/monthly${queryString ? '?' + queryString : ''}`);
  },

  async getYearlyFinance(year?: number, category?: string, itemId?: number): Promise<ApiResponse<FinanceData>> {
    const params = new URLSearchParams();
    if (year) params.append('year', year.toString());
    if (category) params.append('category', category);
    if (itemId) params.append('item_id', itemId.toString());
    const queryString = params.toString();
    return apiService.get(`/api/finance/yearly${queryString ? '?' + queryString : ''}`);
  },

  async getFinanceSummary(): Promise<ApiResponse<FinanceSummary>> {
    return apiService.get('/api/finance/summary');
  },

  async getFinanceRange(startDate: string, endDate: string, category?: string, itemId?: number): Promise<ApiResponse<FinanceData>> {
    const params = new URLSearchParams();
    params.append('start_date', startDate);
    params.append('end_date', endDate);
    if (category) params.append('category', category);
    if (itemId) params.append('item_id', itemId.toString());
    return apiService.get(`/api/finance/range?${params.toString()}`);
  },
};

